import { metricsService } from './metricsService';
import { authService } from './authService';

/**
 * Тонкий клиент для логирования UI-действий пользователя в bit-logger
 * (через прокси /metrics-api/ui-event → bit-metrics → mongodb.ui_events).
 *
 * Fire-and-forget: ошибки сети не ломают UX, просто молча проглатываются.
 *
 * Использование:
 *   logUiEvent('cancel', { component: 'transcription', task_id: 'abc' });
 *   logUiEvent('new',    { component: 'transcription' });
 */
export async function logUiEvent(event, { component, task_id, meta } = {}) {
  try {
    const tokenData = authService.getTokenData();
    const user_email = tokenData?.email || '';
    await metricsService.postUiEvent({
      event,
      user_email,
      component,
      task_id,
      meta,
    });
  } catch (e) {
    // Логирование не должно ломать UX — молча проглатываем.
  }
}

/**
 * Логирует изменения настроек Истории (приватный opt-in): включение/выключение
 * и смену срока хранения. Источник правды о ТЕКУЩЕМ состоянии — user_settings в
 * bit-auth; здесь пишется только append-only временной след действий в ui_events.
 *
 * Вызывается из всех мест, меняющих настройки Истории (профиль + баннер сайдбара),
 * поэтому логика собрана в одну функцию — вызывать ТОЛЬКО после успешного save.
 *
 * - `history_enabled` в fields → событие `history_enabled` / `history_disabled`
 *   (meta.retention — контекст срока на момент действия).
 * - `history_retention` без `history_enabled` → событие `history_retention`.
 *   Условие отсекает баннер включения (шлёт enabled+retention разом — там срок
 *   уже отражён в событии включения, отдельно логировать не нужно).
 *
 * @param {object} fields — патч настроек, ушедший в PATCH /me/settings.
 * @param {string} [currentRetention] — текущий срок (для enable-события, когда
 *   тоггл прислан без retention).
 */
export function logHistorySettingsChange(fields, currentRetention) {
  if ('history_enabled' in fields) {
    logUiEvent(fields.history_enabled ? 'history_enabled' : 'history_disabled', {
      component: 'history',
      meta: { retention: fields.history_retention || currentRetention },
    });
  }
  if ('history_retention' in fields && !('history_enabled' in fields)) {
    logUiEvent('history_retention', {
      component: 'history',
      meta: { retention: fields.history_retention },
    });
  }
}

/**
 * Логирует изменение личного opt-in «Ускорять загрузку видеофайлов»
 * (audio_extract_opt_in, /me/settings) — тот же приём, что у Истории
 * (logHistorySettingsChange): временной след действия в ui_events,
 * источник правды о текущем состоянии остаётся в bit-auth.
 *
 * @param {boolean} enabled — новое значение audio_extract_opt_in.
 */
export function logAudioExtractOptInChange(enabled) {
  logUiEvent('audio_extract_opt_in', { component: 'profile', meta: { enabled } });
}
